import { useEffect, useRef } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SubsetForm } from '../../components/subset-form';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetIssues } from '@/api/queries/useGetSubsetIssues';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';

export function SubsetEditor() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const additionalDocIds: string[] = location.state?.additionalDocIds ?? [];

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset, isLoading: isLoadingSubset } = useGetSubset(matterId, subsetId);
  const { data: docIds, isLoading: isLoadingDocs } = useGetDocIds(matterId ?? '', subsetId ?? '');
  const { data: subsetIssues = [], isLoading: isLoadingIssues } = useGetSubsetIssues(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId: matterId ?? '',
    subsetId: subsetId ?? '',
    matter,
    subset,
    currentPage: { label: 'Edit', path: 'edit' },
  });

  const hasShownToast = useRef(false);
  useEffect(() => {
    if (!docIds || additionalDocIds.length === 0 || hasShownToast.current) return;
    hasShownToast.current = true;

    const existingIds = docIds.documents.map((doc) => doc.id);
    const skipped = additionalDocIds.filter((id) => existingIds.includes(id)).length;
    const adding = additionalDocIds.length - skipped;

    if (adding === 0) {
      toast.info(`All documents (${skipped}) are already in this subset`);
      return;
    }

    const parts: string[] = [];
    parts.push(`${adding} ${adding === 1 ? 'document' : 'documents'} ready to be added`);
    if (skipped > 0) parts.push(`${skipped} already in subset`);

    toast.info(parts.join(', '));
  }, [additionalDocIds, docIds]);

  if (!matterId || !subsetId) {
    return null;
  }

  // Navigate back to subset overview after successful update
  const handleSuccess = (updatedSubsetId: string) => {
    navigate(`/matters/${matterId}/subsets/${updatedSubsetId}`);
  };

  const isLoading = isLoadingSubset || isLoadingDocs || isLoadingIssues;

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb items={breadcrumbItems} />

        <div className="flex h-full items-center justify-center gap-2 text-gray-500">
          <Icon className="lucide--loader mt-px animate-spin ease-in-out" />
          <p>Loading subset data...</p>
        </div>
      </div>
    );
  }

  // Show error state if data is missing
  if (!subset || !docIds) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb items={breadcrumbItems} />

        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <Icon className="lucide--alert-circle h-12 w-12 text-red-500" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Failed to load subset</h3>
              <p className="mt-2 text-gray-500">Unable to retrieve subset data. Please try again.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Transform data into initialData format for SubsetForm
  const existingDocIds = docIds.documents.map((doc) => doc.id);
  const mergedDocIds =
    additionalDocIds.length > 0 ? [...new Set([...existingDocIds, ...additionalDocIds])] : existingDocIds;

  const initialData = {
    name: subset.name,
    issues: subsetIssues.map((i) => ({ id: i.id, label: i.label, text: i.text })),
    selectedDocIds: mergedDocIds,
    baseDocIds: additionalDocIds.length > 0 ? existingDocIds : undefined,
  };

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />

      <SubsetForm
        mode="edit"
        matterId={matterId}
        subsetId={subsetId}
        initialData={initialData}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
